import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import { SubscriptionPlansDto } from '@/Domain/api/Subscription/dto/SubscriptionPlans.dto';
import { AICreditsUsageCard } from '@/Domain/components/Subscription/AICreditsUsageCard/AICreditsUsageCard';
import { BillingPortalCard } from '@/Domain/components/Subscription/BillingPortalCard';
import { DiscordCreditTopupPanel } from '@/Domain/components/Subscription/CreditTopup/DiscordCreditTopupPanel';
import { useCreditTopupCheckoutReturn } from '@/Domain/components/Subscription/CreditTopup/useCreditTopupCheckoutReturn';
import { FailedPaymentAlert } from '@/Domain/components/Subscription/FailedPaymentAlert';
import { MembershipCard } from '@/Domain/components/Subscription/MembershipCard/MembershipCard';
import { OnboardingDiscountBanner } from '@/Domain/components/Subscription/OnboardingDiscountBanner';
import { PricingPlans } from '@/Domain/components/Subscription/PricingPlans/PricingPlans';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { useCreditTopupOffer } from '@/Domain/hooks/Subscription/useCreditTopupOffer.hook';

export type StripeData = {
  currentPlan: SubscriptionDto['current_plan'];
  subscription: SubscriptionDto['subscription'];
  plans: SubscriptionPlansDto['products'];
  pricing: SubscriptionPlansDto['subscriptions'];
};

export const SubscriptionPage = (stripeData: StripeData) => {
  const { currentPlan, subscription } = stripeData;
  const { t } = useTranslation('subscription_page');
  const [isBillingYearly, setIsBillingYearly] = useState(false);
  const { isEligible } = useCreditTopupOffer();

  useCreditTopupCheckoutReturn();

  useLayoutEffect(() => {
    if (stripeData.currentPlan.interval === SubscriptionIntervalEnum.Annually) {
      setIsBillingYearly(true);
    }
  }, [stripeData.currentPlan.interval]);

  return (
    <div>
      {subscription.payment_failed && (
        <div className="mb-4">
          <FailedPaymentAlert />
        </div>
      )}
      <OnboardingDiscountBanner />
      <div className="flex flex-col gap-18">
        <DiscordCreditTopupPanel />

        <section>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <MembershipCard subscription={subscription} currentPlan={currentPlan} />
            <AICreditsUsageCard
              tier={currentPlan.tier}
              nextPaymentOn={subscription.next_payment_on}
            />
            <BillingPortalCard
              isPaymentFailed={subscription.payment_failed}
              isFreeAccount={SubscriptionHelper.isFreeAccount(currentPlan.tier)}
            />
          </div>
        </section>

        <section className="flex flex-col gap-6">
          {isEligible && (
            <p className="text-sm text-muted-foreground" data-testid="pricing-topup-lead-in">
              {t('pricing.plans_lead_in')}
            </p>
          )}
          <PricingPlans
            stripeData={stripeData}
            isBillingYearly={isBillingYearly}
            onBillingYearlyChange={setIsBillingYearly}
          />
        </section>
      </div>
    </div>
  );
};
